import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Newspaper, CalendarDays, Trophy, Users, ImagePlus, Inbox, LogOut, ChevronRight, Lock, Key,
  Edit, Handshake, Phone
} from "lucide-react";
import NewRocznikManager from "./admin/newRocznikManager";
import NewsManager from "./admin/NewsManager";
import EventsManager from "./admin/EventsManager";
import PhotoManager from "./photoManager";
import NewPhotoManager from "./admin/PhotoManager";
import AdminInbox from "./admin/newAdminInbox";
import SponsorsManager from "./admin/sponsorsManager";
import ContactManager from "./admin/ContactManager";
// --- STYLED COMPONENTS (GŁÓWNY PANEL) ---
const AdminLayout = styled.div` display: flex; min-height: 100vh; background-color: #f4f6f8; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; `;
const Sidebar = styled.aside` width: 280px; background-color: #111; color: #fff; display: flex; flex-direction: column; position: fixed; height: 100vh; left: 0; top: 0; z-index: 100; `;
const SidebarHeader = styled.div` padding: 30px 20px; border-bottom: 1px solid #333; h2 { margin: 0; font-weight: 900; text-transform: uppercase; font-size: 1.5rem; letter-spacing: 1px; span { color: #888; } } `;
const NavList = styled.nav` display: flex; flex-direction: column; padding: 20px 0; flex: 1; overflow-y: auto; `;
const NavItem = styled.button` display: flex; align-items: center; gap: 15px; padding: 15px 25px; background: ${props => props.active ? '#222' : 'transparent'}; color: ${props => props.active ? '#fff' : '#888'}; border: none; border-left: 4px solid ${props => props.active ? '#fff' : 'transparent'}; width: 100%; text-align: left; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.3s ease; &:hover { background: #222; color: #fff; } svg { width: 20px; height: 20px; } `;
const LogoutButton = styled.button` display: flex; align-items: center; gap: 15px; padding: 20px 25px; width: 100%; text-align: left; background: #000; color: #ff4d4d; border: none; font-size: 1rem; cursor: pointer; font-weight: bold; border-top: 1px solid #333; transition: background 0.3s; &:hover { background: #1a0000; } `;
const MainContent = styled.main` flex: 1; margin-left: 280px; padding: 40px 5%; `;
const TopBar = styled.header` display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px; h1 { font-size: 2.2rem; font-weight: 800; color: #111; margin: 0; letter-spacing: -1px; } .user-info { color: #666; font-weight: 600; } `;
const DashboardGrid = styled.div` display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 25px; `;
const ActionCard = styled.div` background: #fff; border-radius: 12px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.04); cursor: pointer; transition: all 0.3s ease; border: 1px solid #eee; display: flex; flex-direction: column; align-items: flex-start; &:hover { transform: translateY(-5px); box-shadow: 0 8px 25px rgba(0,0,0,0.08); border-color: #ddd; } .icon-wrapper { width: 50px; height: 50px; background: #f4f6f8; border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; color: #111; } h3 { margin: 0 0 10px 0; font-size: 1.2rem; font-weight: 800; color: #111; } p { margin: 0; color: #666; font-size: 0.95rem; line-height: 1.5; flex: 1; } .arrow { margin-top: 20px; align-self: flex-end; color: #999; transition: color 0.3s, transform 0.3s; } &:hover .arrow { color: #111; transform: translateX(5px); } `;

// --- STYLED COMPONENTS (LOGOWANIE) ---
const LoginContainer = styled.div` min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #f4f6f8; `;
const LoginBox = styled.form` background: #fff; padding: 40px; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.05); width: 100%; max-width: 400px; display: flex; flex-direction: column; gap: 20px; text-align: center; h2 { margin: 0 0 10px 0; font-weight: 900; font-size: 1.8rem; } input { padding: 15px; border: 1px solid #ddd; border-radius: 8px; font-size: 1.1rem; text-align: center; &:focus { outline: none; border-color: #111; } } button { background: #111; color: #fff; padding: 15px; border: none; border-radius: 8px; font-size: 1.1rem; font-weight: bold; cursor: pointer; transition: 0.3s; &:hover { background: #333; } } .error { color: red; font-size: 0.9rem; font-weight: bold; margin: 0; } `;

// --- KOMPONENT ZMIANY HASŁA ---
const SettingsContainer = styled.div` background: #fff; padding: 30px; border-radius: 12px; border: 1px solid #eee; max-width: 500px; display: flex; flex-direction: column; gap: 20px; `;

const PasswordChanger = ({ token }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [msg, setMsg] = useState("");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch("https://api.pth-hokej.pl/api/admin/password", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ oldPassword, newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setMsg("✅ " + data.message);
        setOldPassword("");
        setNewPassword("");
      } else {
        setMsg("❌ " + data.error);
      }
    } catch (err) {
      setMsg("❌ Błąd połączenia z serwerem.");
    }
  };

  return (
    <SettingsContainer>
      <h3><Key size={20} style={{ marginRight: '10px', verticalAlign: 'middle' }} /> Zmiana Hasła</h3>
      <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Obecne hasło</label>
          <input type="password" required value={oldPassword} onChange={e => setOldPassword(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Nowe hasło</label>
          <input type="password" required minLength="3" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
        </div>
        <button type="submit" style={{ background: '#111', color: '#fff', padding: '10px', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Zmień Hasło</button>
        {msg && <p style={{ margin: 0, fontWeight: 'bold' }}>{msg}</p>}
      </form>
    </SettingsContainer>
  );
};

// --- GŁÓWNY KOMPONENT ADMINA ---
export const Admin = () => {
  const [messages, setMessages] = useState([]);

  const adresAPI = "https://api.pth-hokej.pl";

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${adresAPI}/api/messages`);
      const data = await res.json();
      setMessages(data);
    } catch (err) {
      console.error("Błąd pobierania wiadomości:", err);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  // Stan Logowania
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Sprawdzanie czy mamy token w pamięci po odświeżeniu
  useEffect(() => {
    const savedToken = localStorage.getItem("adminToken");
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  // Obsługa Logowania
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch("https://api.pth-hokej.pl/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: loginPassword })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("adminToken", data.token);
        setToken(data.token);
        setIsAuthenticated(true);
        setLoginError("");
      } else {
        setLoginError(data.error || "Błąd logowania");
      }
    } catch (err) {
      setLoginError("Błąd serwera.");
    }
  };

  // Obsługa Wylogowania
  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setToken(null);
    setIsAuthenticated(false);
    navigate("/");
  };

  // Routing wewnętrzny Panelu
  const pathParts = location.pathname.split('/');
  const activeTab = pathParts[2] || 'dashboard';
  const handleTabChange = (tab) => {
    if (tab === 'visual_edit') {
      window.location.href = "/"; // Przekierowanie na stronę główną z aktywnym tokenem
      return;
    }
    if (tab === 'dashboard') navigate('/admin');
    else navigate(`/admin/${tab}`);
    window.scrollTo(0, 0);
  };
  const menuItems = [
    { id: 'dashboard', label: 'Kokpit Główny', icon: <LayoutDashboard />, desc: 'Przegląd całego systemu' },
    { id: 'news', label: 'Aktualności', icon: <Newspaper />, desc: 'Dodawaj, edytuj i usuwaj wpisy na blogu' },
    { id: 'events', label: 'Wydarzenia', icon: <CalendarDays />, desc: 'Zarządzaj nadchodzącymi wydarzeniami' },
    // { id: 'matches', label: 'Mecze', icon: <Trophy />, desc: 'Terminarz spotkań i przeciwnicy' },
    // { id: 'teams', label: 'Roczniki', icon: <Users />, desc: 'Trenerzy, zawodnicy i dni treningów' },
    { id: 'gallery', label: 'Galeria Zdjęć', icon: <ImagePlus />, desc: 'Wgrywaj nowe zdjęcia do galerii' },
    { id: 'inbox', label: `Wiadomości - ${Array.isArray(messages) ? messages.length : 0}`, icon: <Inbox />, desc: 'Formularze zgłoszeniowe i kontakt' },
    { id: 'settings', label: 'Ustawienia', icon: <Lock />, desc: 'Zmiana hasła administratora' },
    { id: 'visual_edit', label: 'Edycja Treści', icon: <Edit />, desc: 'Przejdź do strony głównej i edytuj teksty na żywo' },
    { id: 'sponsors', label: 'Sponsorzy', icon: <Handshake />, desc: 'Zarządzaj partnerami klubu i ich logotypami' }, // <--- NOWY ELEMENT
    { id: 'contact', label: 'Kontakt i Sociale', icon: <Phone />, desc: 'Zarządzaj danymi kontaktowymi i linkami do social mediów' },
  ];

  // Jeśli użytkownik nie jest zalogowany - pokaż formularz
  if (!isAuthenticated) {
    return (
      <LoginContainer>
        <LoginBox onSubmit={handleLogin}>
          <Lock size={40} style={{ margin: '0 auto', color: '#111' }} />
          <h2>Admin Panel
          </h2>
          <input
            type="password"
            placeholder="Podaj hasło"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            required
          />
          {loginError && <p className="error">{loginError}</p>}
          <button type="submit">Zaloguj się</button>
          <Link to="/" style={{ color: '#666', fontSize: '0.9rem', marginTop: '10px' }}>&larr; Wróć na stronę główną</Link>
        </LoginBox>
      </LoginContainer>
    )
  }

  // Jeśli jest zalogowany - pokaż panel
  return (
    <AdminLayout>
      <Sidebar>
        <SidebarHeader>
          <h2>PTH Koziołki <span>Admin</span></h2>
        </SidebarHeader>
        <NavList>
          {menuItems.map(item => (
            <NavItem
              key={item.id}
              active={activeTab === item.id}
              onClick={() => handleTabChange(item.id)}
            >
              {item.icon}
              {item.label}
            </NavItem>
          ))}
        </NavList>
        <LogoutButton onClick={handleLogout}>
          <LogOut />
          Wyloguj się
        </LogoutButton>
      </Sidebar>

      <MainContent>
        <TopBar>
          <h1>
            {menuItems.find(item => item.id === activeTab)?.label || 'Panel Administratora'}
          </h1>
          <div className="user-info">Zalogowano jako: Administrator</div>
        </TopBar>

        {activeTab === 'dashboard' && (
          <DashboardGrid>
            {menuItems.filter(item => item.id !== 'dashboard').map(item => (
              <ActionCard key={item.id} onClick={() => handleTabChange(item.id)}>
                <div className="icon-wrapper">
                  {item.icon}
                </div>
                <h3>{item.label}</h3>
                <p>{item.desc}</p>
                <ChevronRight className="arrow" />
              </ActionCard>
            ))}
          </DashboardGrid>
        )}

        {activeTab === 'news' && <div><NewsManager /></div>}
        {activeTab === 'events' && <div><EventsManager /></div>}
        {/* {activeTab === 'matches' && <div>TUTAJ WRZUCIMY KOMPONENT MECZÓW</div>} */}
        {/* {activeTab === 'teams' && <div><NewRocznikManager /></div>} */}
        {activeTab === 'gallery' && <div><NewPhotoManager /></div>}
        {activeTab === 'inbox' && <div><AdminInbox /></div>}
        {activeTab === 'settings' && <PasswordChanger token={token} />}
        {activeTab === 'sponsors' && <div><SponsorsManager /></div>}
        {activeTab === 'contact' && <div><ContactManager /></div>}

      </MainContent>
    </AdminLayout>
  );
};